import React, { useLayoutEffect, useRef, useState } from 'react';

import styles from './Tooltip.module.scss';

interface TooltipProps {
  content: Record<string, { label: string; value: string }>;
  position: number;
  clipPath: string;
  chartWidth: number;
  chartMargin: { top: number; right: number; bottom: number; left: number };
}

export const Tooltip = ({
  content,
  position,
  clipPath,
  chartWidth,
  chartMargin
}: TooltipProps) => {
  const textRef = useRef<SVGTextElement[]>([]);
  const [boxWidth, setBoxWidth] = useState(120);
  const padding = 6;
  const lineHeight = 16;

  const rightBound = position + boxWidth + padding * 2;
  const currBoxAlign =
    rightBound >= chartWidth ? chartWidth - rightBound + padding : padding;

  useLayoutEffect(() => {
    if (textRef.current && Object.keys(content).length !== 0) {
      const widths = textRef.current.map(
        (el) => el.getBoundingClientRect().width
      );
      const maxWidth = Math.max(...widths);
      const newSize = Math.round(maxWidth + padding * 2);
      if (boxWidth !== newSize) {
        setBoxWidth(newSize);
      }
    }
  }, [content, boxWidth]);

  const InfoBox = () => {
    const keys = Object.keys(content);
    const contentLength = keys.length;
    const boxHeight = contentLength * lineHeight + padding + 2;

    return (
      <g className={styles.infoBox} transform={`translate(${currBoxAlign}, 0)`}>
        <rect x={0} y={2} height={boxHeight} width={boxWidth} />
        {keys.map((stat, index) => {
          const contentItem = content[stat];
          if (!contentItem) {
            return null;
          }

          function setRef(el: SVGTextElement) {
            textRef.current[index] = el;
          }

          return (
            <text
              ref={setRef}
              key={stat}
              dy="1em"
              transform={`translate(${padding}, ${
                padding + lineHeight * index
              })`}
            >
              <tspan>{contentItem.label}</tspan>
              <tspan className={styles.value} dx={5}>
                {contentItem.value}
              </tspan>
            </text>
          );
        })}
      </g>
    );
  };

  return (
    <g className={styles.group} transform={`translate(${position}, 0)`}>
      <line
        className={styles.marker}
        y1={-chartMargin.top}
        y2="100%"
        x1={1}
        x2={1}
        clipPath={clipPath}
      />
      <InfoBox />
    </g>
  );
};
